import { createRouteMatcher } from './matcher';
import type { PreparedRoute, RouteCoreContract, RouteDefinition, RouteMatch, RouteSnapshot } from './types';
import { RouteRedirect } from './types';
import { signal } from '@preact/signals-core';
import { resolvePath, toLocation } from 'src/lib/navigation/path';
import type { AppLocation } from 'src/lib/navigation/types';

const MAX_REDIRECTS = 16;

const abortError = () => new DOMException('The route entry was superseded', 'AbortError');

const throwIfAborted = (abortSignal: AbortSignal) => {
    if (abortSignal.aborted) throw abortError();
};

interface EnteredMatch<TContainer, TLoaded> {
    readonly match: RouteMatch<TContainer, TLoaded>;
    readonly disposables: DisposableStack;
}

export class RouteCore<TContainer, TLoaded> implements RouteCoreContract<TContainer, TLoaded> {
    readonly current = signal<RouteSnapshot<TContainer, TLoaded> | null>(null);
    readonly pendingLocation = signal<AppLocation | null>(null);
    readonly #container: TContainer;
    readonly #match;
    #active: readonly EnteredMatch<TContainer, TLoaded>[] = [];
    #pendingCount = 0;

    constructor(definitions: readonly RouteDefinition<TContainer, TLoaded>[], container: TContainer) {
        this.#container = container;
        this.#match = createRouteMatcher(definitions);
    }

    async prepare(initialLocation: AppLocation, abortSignal: AbortSignal): Promise<PreparedRoute<TContainer, TLoaded>> {
        const pendingId = ++this.#pendingCount;
        this.pendingLocation.value = initialLocation;
        let location = initialLocation;
        let replace = false;

        try {
            for (let redirects = 0; redirects <= MAX_REDIRECTS; redirects += 1) {
                throwIfAborted(abortSignal);
                try {
                    const entered = await this.#enter(location, abortSignal);
                    const snapshot: RouteSnapshot<TContainer, TLoaded> = {
                        location,
                        matches: entered.map(({ match }) => match),
                        params: entered.at(-1)?.match.params ?? {},
                    };
                    let settled = false;

                    return {
                        location,
                        replace,
                        snapshot,
                        commit: (committedLocation = location) => {
                            if (settled) return;
                            settled = true;
                            if (pendingId !== this.#pendingCount) {
                                this.#disposeEntered(entered);
                                return;
                            }
                            this.#disposeActive();
                            this.#active = entered;
                            this.current.value =
                                committedLocation === location
                                    ? snapshot
                                    : { ...snapshot, location: committedLocation };
                            this.pendingLocation.value = null;
                        },
                        [Symbol.dispose]: () => {
                            if (settled) return;
                            settled = true;
                            this.#disposeEntered(entered);
                            if (pendingId === this.#pendingCount) this.pendingLocation.value = null;
                        },
                    };
                } catch (error) {
                    if (!(error instanceof RouteRedirect)) throw error;
                    replace ||= error.replace;
                    location = toLocation(resolvePath(error.to, location.pathname), location);
                    if (redirects === MAX_REDIRECTS) {
                        throw new Error(`Route redirect limit of ${MAX_REDIRECTS} exceeded`);
                    }
                }
            }

            throw new Error('Unreachable route redirect state');
        } catch (error) {
            if (pendingId === this.#pendingCount) this.pendingLocation.value = null;
            throw error;
        }
    }

    async #enter(location: AppLocation, abortSignal: AbortSignal): Promise<EnteredMatch<TContainer, TLoaded>[]> {
        const branch = this.#match(location.pathname);
        const entered: EnteredMatch<TContainer, TLoaded>[] = [];

        try {
            for (const [index, definition] of branch.definitions.entries()) {
                throwIfAborted(abortSignal);
                const disposables = new DisposableStack();
                try {
                    const [loaded] = await Promise.all([
                        definition.load?.(),
                        definition.enter?.({
                            container: this.#container,
                            location,
                            params: branch.params,
                            signal: abortSignal,
                            register(disposable) {
                                if (abortSignal.aborted) {
                                    disposable[Symbol.dispose]();
                                    throw abortError();
                                }
                                disposables.use(disposable);
                            },
                        }),
                    ]);
                    throwIfAborted(abortSignal);
                    entered.push({
                        disposables,
                        match: {
                            definition,
                            loaded: loaded ?? null,
                            params: branch.params,
                            pathnameBase: branch.pathnameBases[index]!,
                        },
                    });
                } catch (error) {
                    disposables[Symbol.dispose]();
                    throw error;
                }
            }
            return entered;
        } catch (error) {
            this.#disposeEntered(entered);
            throw error;
        }
    }

    #disposeEntered(entered: readonly EnteredMatch<TContainer, TLoaded>[]): void {
        for (const { disposables } of entered.toReversed()) {
            disposables[Symbol.dispose]();
        }
    }

    #disposeActive(): void {
        this.#disposeEntered(this.#active);
        this.#active = [];
    }

    [Symbol.dispose](): void {
        this.#pendingCount += 1;
        this.pendingLocation.value = null;
        this.#disposeActive();
        this.current.value = null;
    }
}
